import { Grid } from '../../../../Common/Grid'
import { Alert, Button, Input } from "@fullscreendigital/carrefour-dex-fe"
import React, { useMemo } from 'react'
import { getValidationSchema, initialValues } from './helpers'
import { useFormik } from 'formik'
import { formatErrorMessage } from '../../../../../../utils/formatting'
import { useNotification } from '../../../../../../hooks'
import { TitleHeader } from '../../../Common/TitleHeader'
import { useQuery } from 'react-query'
import { makeCoreGetRequest, makeCorePostRequest } from '../../../../../../utils/api'
import { CircularProgress } from '@mui/material'

type Supplier = {
  supplierCode: number | string
  supplierName: string
}

type Support = {
  id: number
  code: string
  name: string
}

const InventoryUpdate = () => {
  const { showNotification } = useNotification()

  const {
    data: shopCode,
    isLoading: isLoadingShopCode,
    isError: isShopCodeError
  } = useQuery(
    'shopCode',
    async () => {
      const response = await makeCoreGetRequest('/shop-reception-mobile/home/shop-code', null)
      return response.value
    },
    {
      onError: error => {
        showNotification({
          message: `Error while fetching shop code. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      },
      staleTime: 5 * 60 * 1000
    }
  )

  const validationSchema = useMemo(() => getValidationSchema(), [])
  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      ...initialValues,
      shopCode: shopCode || null,
    },
    validationSchema: validationSchema,
    onSubmit: async (values: any) => {
      try {
        await makeCorePostRequest('/shop-reception-mobile/ds-support-stock-inventory', values)
        showNotification({
          message: `Inventar actualizat cu succes.`,
          type: 'success'
        })

        formik.resetForm({
          values: {
            ...initialValues,
            shopCode: shopCode || null
          }
        })
      } catch (error) {
        showNotification({
          message: `Error while submitting. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  })

  const {
    data: supplierList,
    isLoading: isLoadingSuppliers,
    isError: isSuppliersError
  } = useQuery<Supplier[]>(
    'ds-supplier-list',
    async () => {
      const resp = await makeCoreGetRequest('/shop-reception-mobile/ds-return-truck/ds-suppliers-list', null)
      return resp as Supplier[]
    },
    {
      onError: error => {
        showNotification({
          message: `Error while fetching suppliers. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const {
    data: supportList,
    isLoading: isLoadingSupports
  } = useQuery<Support[]>(
    ['ds-supportList', formik.values.supplierCode],
    async () => {
      return await makeCoreGetRequest('/shop-reception-mobile/ds-order/ds-support-type/list', null)
    },
    {
      enabled: !!formik.values.supplierCode,
      onSuccess: () => {
        if (formik.values.dsSupportTypeId) {
          formik.setFieldValue('dsSupportTypeId', null)
        }
      },
      onError: error => {
        showNotification({
          message: `Error while fetching supports. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  if (isLoadingShopCode || isLoadingSuppliers) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '40px' }}>
        <CircularProgress color="primary" disableShrink />
      </div>
    )
  }

  if (isShopCodeError || isSuppliersError) {
    return (
      <Grid
        items={
          <Alert severity="error">
            A apărut o eroare la încărcarea datelor. Vă rugăm să reîncărcați pagina.
          </Alert>
        }
        itemsPerRow={1}
        gridContainerStyle={{ marginTop: '20px' }}
        maxWidth="xs"
      />
    )
  }

  const isSubmitDisabled =
    formik.isSubmitting ||
    !formik.values.supplierCode ||
    !formik.values.dsSupportTypeId ||
    formik.values.stock === null ||
    formik.values.stock === undefined

  return (
    <Grid
      gridContainerStyle={{ marginTop: '20px' }}
      maxWidth="xs"
      items={[
        <TitleHeader title="ACTUALIZARE INVENTAR AMBALAJE" />,

        <Input
          key="supplier"
          type="select"
          label="Furnizori Directi"
          variant="standard"
          width="100%"
          value={formik.values.supplierCode || ''}
          values={supplierList?.map(s => ({
            value: s.supplierCode,
            label: s.supplierName
          })) || []}
          onChange={event => {
            const selectedValue = (event.target as HTMLInputElement).value
            formik.setFieldValue('supplierCode', selectedValue ? Number(selectedValue) : null)
          }}
          disabled={isLoadingSuppliers}
          error={!!formik.errors.supplierCode}
          helperText={formik.errors.supplierCode as string}
        />,

        <Input
          key="support"
          type="select"
          label="Tip suport"
          variant="standard"
          width="100%"
          value={formik.values.dsSupportTypeId || ''}
          values={supportList?.map(support => ({
            value: support.id,
            label: support.code + ' - ' + support.name
          })) || []}
          onChange={event => {
            const selectedValue = (event.target as HTMLInputElement).value
            formik.setFieldValue('dsSupportTypeId', selectedValue || null)
          }}
          disabled={!formik.values.supplierCode || isLoadingSupports}
          error={!!formik.errors.dsSupportTypeId}
          helperText={
            isLoadingSupports
              ? 'Se încarcă...'
              : (formik.errors.dsSupportTypeId as string)
          }
        />,

        <Input
          key="stock"
          type="number"
          label="Stoc"
          variant="standard"
          width="100%"
          value={formik.values.stock ?? ''}
          onChange={event => {
            const value = (event.target as HTMLInputElement).value
            formik.setFieldValue('stock', value === '' ? null : Number(value))
          }}
          error={!!formik.errors.stock}
          helperText={formik.errors.stock as string}
        />,

        <Button
          key="submit"
          type="button"
          variant="contained"
          label="CONFIRMǍ"
          style={{ width: '100%' }}
          onClick={() => formik.handleSubmit()}
          disabled={isSubmitDisabled}
        />
      ]}
      itemsPerRow={1}
    />
  )
}

export default InventoryUpdate
